import { useState, useEffect } from "react";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getDealerSubmittedLocations, getDealerAssignedLocations, updateLocationContacts, updateLocationVidapay } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Clock, Phone, Mail, CheckCircle, AlertCircle, Loader2, Building2, Zap, User, Edit, Save, X, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_CONFIG = {
  new: { label: "Pending Review", color: "bg-amber-100 text-amber-800", icon: Clock },
  contacted: { label: "Contacted", color: "bg-blue-100 text-blue-800", icon: Phone },
  qualified: { label: "Qualified", color: "bg-indigo-100 text-indigo-800", icon: CheckCircle },
  converted: { label: "Approved", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  lost: { label: "Declined", color: "bg-red-100 text-red-800", icon: AlertCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function ContactEditor({ location, onSave }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    district_manager_name: location.district_manager_name || "",
    district_manager_phone: location.district_manager_phone || "",
    district_manager_email: location.district_manager_email || "",
    store_manager_name: location.store_manager_name || "",
    store_manager_phone: location.store_manager_phone || "",
    store_manager_email: location.store_manager_email || "",
  });

  const hasDM = location.district_manager_name || location.district_manager_phone || location.district_manager_email;
  const hasSM = location.store_manager_name || location.store_manager_phone || location.store_manager_email;
  const hasAny = hasDM || hasSM;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateLocationContacts(location.id, form);
      toast.success("Contacts updated");
      setEditing(false);
      onSave();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update contacts"); }
    finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <div className="mt-3 ml-12 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`contacts-${location.id}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <User className="w-3.5 h-3.5 text-navy" />
            <span className="text-xs font-semibold text-navy">Management Contacts</span>
          </div>
          <button onClick={() => setEditing(true)} className="text-xs text-teal hover:underline flex items-center gap-1" data-testid={`edit-contacts-${location.id}`}>
            <Edit className="w-3 h-3" /> Edit
          </button>
        </div>
        {!hasAny ? (
          <p className="text-xs text-slate-400">No contacts set. Click Edit to add district and store managers.</p>
        ) : (
          <div className="space-y-2">
            <div>
              <p className="text-xs text-slate-400 font-medium">District Manager</p>
              <p className="text-sm text-slate-700">{location.district_manager_name || "—"} {location.district_manager_phone ? `· ${location.district_manager_phone}` : ""} {location.district_manager_email ? `· ${location.district_manager_email}` : ""}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium">Store Manager</p>
              <p className="text-sm text-slate-700">{location.store_manager_name || "—"} {location.store_manager_phone ? `· ${location.store_manager_phone}` : ""} {location.store_manager_email ? `· ${location.store_manager_email}` : ""}</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 ml-12 p-4 bg-white rounded-lg border-2 border-teal/30" data-testid={`contacts-edit-${location.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <User className="w-3.5 h-3.5 text-navy" />
          <span className="text-xs font-semibold text-navy">Edit Management Contacts</span>
        </div>
        <button onClick={() => setEditing(false)} className="text-xs text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-3">
        <div>
          <p className="text-xs font-medium text-indigo-600 mb-2">District Manager</p>
          <div className="grid grid-cols-3 gap-2">
            <Input value={form.district_manager_name} onChange={(e) => setForm({ ...form, district_manager_name: e.target.value })} placeholder="Name" className="text-sm h-9" data-testid="dealer-dm-name" />
            <Input value={form.district_manager_phone} onChange={(e) => setForm({ ...form, district_manager_phone: e.target.value })} placeholder="Phone" className="text-sm h-9" data-testid="dealer-dm-phone" />
            <Input value={form.district_manager_email} onChange={(e) => setForm({ ...form, district_manager_email: e.target.value })} placeholder="Email" className="text-sm h-9" data-testid="dealer-dm-email" />
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-teal mb-2">Store Manager</p>
          <div className="grid grid-cols-3 gap-2">
            <Input value={form.store_manager_name} onChange={(e) => setForm({ ...form, store_manager_name: e.target.value })} placeholder="Name" className="text-sm h-9" data-testid="dealer-sm-name" />
            <Input value={form.store_manager_phone} onChange={(e) => setForm({ ...form, store_manager_phone: e.target.value })} placeholder="Phone" className="text-sm h-9" data-testid="dealer-sm-phone" />
            <Input value={form.store_manager_email} onChange={(e) => setForm({ ...form, store_manager_email: e.target.value })} placeholder="Email" className="text-sm h-9" data-testid="dealer-sm-email" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
          <Button size="sm" className="bg-teal hover:bg-teal/90 text-white" onClick={handleSave} disabled={saving} data-testid="save-contacts-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function RevealableSecret({ value, testid }) {
  const [shown, setShown] = useState(false);
  if (!value) return <p className="font-medium text-slate-700">—</p>;
  return (
    <p className="font-medium text-slate-700 inline-flex items-center gap-1.5" data-testid={testid}>
      <span className="font-mono">{shown ? value : "••••••••"}</span>
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        className="text-slate-400 hover:text-blue-600"
        aria-label={shown ? "Hide" : "Show"}
        data-testid={`${testid}-toggle`}
      >
        {shown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
    </p>
  );
}

function ExternalSystemsEditor({ location, onSave }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vidapay_username: location.vidapay_username || "",
    vidapay_password: "",
    b2bsoft_client_id: location.b2bsoft_client_id || "",
    b2bsoft_access_code: location.b2bsoft_access_code || "",
    b2bsoft_username: location.b2bsoft_username || "",
    b2bsoft_password: "",
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        vidapay_username: form.vidapay_username,
        b2bsoft_client_id: form.b2bsoft_client_id,
        b2bsoft_access_code: form.b2bsoft_access_code,
        b2bsoft_username: form.b2bsoft_username,
      };
      // Only send passwords when the owner typed a new one; blank keeps existing
      if (form.vidapay_password) payload.vidapay_password = form.vidapay_password;
      if (form.b2bsoft_password) payload.b2bsoft_password = form.b2bsoft_password;
      await updateLocationVidapay(location.id, payload);
      toast.success("External systems credentials updated");
      setEditing(false);
      setForm({ ...form, vidapay_password: "", b2bsoft_password: "" });
      onSave();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update external systems");
    } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="mt-2 text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
        data-testid={`edit-external-systems-${location.id}`}
      >
        <Edit className="w-3 h-3" /> Edit External Systems credentials
      </button>
    );
  }

  return (
    <div className="mt-3 p-3 bg-white rounded-lg border-2 border-slate-200 space-y-4" data-testid={`external-systems-edit-${location.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Zap className="w-3.5 h-3.5 text-slate-600" />
          <span className="text-xs font-semibold text-slate-700">Edit External Systems</span>
        </div>
        <button onClick={() => setEditing(false)} className="text-xs text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>

      {/* VidaPay */}
      <div className="p-3 bg-blue-50/60 rounded-lg border border-blue-100">
        <div className="flex items-center gap-1.5 mb-2">
          <Zap className="w-3 h-3 text-blue-600" />
          <span className="text-[11px] font-semibold text-blue-700">VidaPay</span>
        </div>
        <p className="text-[11px] text-slate-500 mb-2">
          TSP ID and status are managed by the admin and cannot be changed here.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[11px] text-slate-500">TSP ID (read only)</Label>
            <Input value={location.vidapay_tsp_id || "—"} disabled className="text-sm h-9 bg-slate-50 text-slate-500" data-testid={`vidapay-tsp-readonly-${location.id}`} />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Username</Label>
            <Input
              value={form.vidapay_username}
              onChange={(e) => setForm({ ...form, vidapay_username: e.target.value })}
              placeholder="VidaPay username"
              className="text-sm h-9"
              data-testid={`vidapay-username-input-${location.id}`}
            />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] text-slate-500">
              New Password {location.vidapay_password ? "(leave blank to keep current)" : ""}
            </Label>
            <Input
              type="password"
              value={form.vidapay_password}
              onChange={(e) => setForm({ ...form, vidapay_password: e.target.value })}
              placeholder={location.vidapay_password ? "••••••••" : "Enter password"}
              className="text-sm h-9"
              data-testid={`vidapay-password-input-${location.id}`}
            />
          </div>
        </div>
      </div>

      {/* B2BSoft */}
      <div className="p-3 bg-purple-50/60 rounded-lg border border-purple-100">
        <div className="flex items-center gap-1.5 mb-2">
          <Zap className="w-3 h-3 text-purple-600" />
          <span className="text-[11px] font-semibold text-purple-700">B2BSoft</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[11px] text-slate-500">Client ID</Label>
            <Input
              value={form.b2bsoft_client_id}
              onChange={(e) => setForm({ ...form, b2bsoft_client_id: e.target.value })}
              placeholder="B2BSoft Client ID"
              className="text-sm h-9"
              data-testid={`b2bsoft-client-id-input-${location.id}`}
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Access Code</Label>
            <Input
              value={form.b2bsoft_access_code}
              onChange={(e) => setForm({ ...form, b2bsoft_access_code: e.target.value })}
              placeholder="B2BSoft Access Code"
              className="text-sm h-9"
              data-testid={`b2bsoft-access-code-input-${location.id}`}
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Username</Label>
            <Input
              value={form.b2bsoft_username}
              onChange={(e) => setForm({ ...form, b2bsoft_username: e.target.value })}
              placeholder="B2BSoft username"
              className="text-sm h-9"
              data-testid={`b2bsoft-username-input-${location.id}`}
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">
              New Password {location.b2bsoft_password ? "(leave blank to keep current)" : ""}
            </Label>
            <Input
              type="password"
              value={form.b2bsoft_password}
              onChange={(e) => setForm({ ...form, b2bsoft_password: e.target.value })}
              placeholder={location.b2bsoft_password ? "••••••••" : "Enter password"}
              className="text-sm h-9"
              data-testid={`b2bsoft-password-input-${location.id}`}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
        <Button size="sm" className="bg-navy hover:bg-navy-dark text-white" onClick={handleSave} disabled={saving} data-testid={`save-external-systems-${location.id}`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save
        </Button>
      </div>
    </div>
  );
}

export default function DealerLocations() {
  const { user } = useAuth();
  const isOwner = !user?.employee_id;
  const [submitted, setSubmitted] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("assigned");

  const fetchAll = async () => {
    try {
      const [sub, asgn] = await Promise.all([
        getDealerSubmittedLocations(),
        getDealerAssignedLocations(),
      ]);
      setSubmitted(sub);
      setAssigned(asgn);
    } catch (err) {
      console.error("Error fetching locations:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const parseNotes = (notes) => {
    if (!notes) return {};
    const lines = notes.split("\n");
    const data = {};
    lines.forEach((line) => {
      const match = line.match(/^(Store Name|Address|City|State|ZIP|Phone|Hours|Description):\s*(.+)/);
      if (match) data[match[1].toLowerCase().replace(/ /g, "_")] = match[2].trim();
    });
    return data;
  };

  if (loading) {
    return (
      <DealerLayout title="My Locations">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal" />
        </div>
      </DealerLayout>
    );
  }

  return (
    <DealerLayout title="My Locations">
      <div className="max-w-4xl mx-auto" data-testid="dealer-locations-page">
        <div className="mb-6">
          <h1 className="font-heading font-bold text-2xl text-navy">My Locations</h1>
          <p className="text-slate-500 font-body text-sm mt-1">View your assigned stores and submitted location requests</p>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("assigned")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "assigned" ? "bg-teal text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
            data-testid="tab-assigned-locations"
          >
            <Building2 className="w-4 h-4 inline mr-1.5" />
            My Stores ({assigned.length})
          </button>
          <button
            onClick={() => setActiveTab("submitted")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === "submitted" ? "bg-teal text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
            data-testid="tab-submitted-locations"
          >
            <Clock className="w-4 h-4 inline mr-1.5" />
            Submissions ({submitted.length})
          </button>
        </div>

        {activeTab === "assigned" && (
          <div className="space-y-3" data-testid="assigned-locations-list">
            {assigned.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 font-body text-lg">No locations assigned yet</p>
                  <p className="text-slate-400 font-body text-sm mt-1">Locations will appear here once an admin assigns them to your account</p>
                </CardContent>
              </Card>
            ) : (
              assigned.map((loc, i) => (
                <motion.div key={loc.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card className="border-slate-200 hover:border-teal/30 transition-colors" data-testid={`assigned-location-${loc.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-5 h-5 text-teal" />
                            </div>
                            <div>
                              <h3 className="font-heading font-semibold text-navy">{loc.name}</h3>
                              <p className="text-sm text-slate-500 font-body">{loc.address}, {loc.city}, {loc.state} {loc.zip_code}</p>
                            </div>
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3 mt-3 pl-12">
                            {loc.phone && (
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Phone className="w-3.5 h-3.5 text-slate-400" />
                                <span>{loc.phone}</span>
                              </div>
                            )}
                            {loc.email && (
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Mail className="w-3.5 h-3.5 text-slate-400" />
                                <span>{loc.email}</span>
                              </div>
                            )}
                            {loc.hours && (
                              <div className="flex items-start gap-2 text-sm text-slate-600 sm:col-span-2">
                                <Clock className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                                <span className="whitespace-pre-line">{loc.hours}</span>
                              </div>
                            )}
                          </div>
                          {loc.services && loc.services.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-3 pl-12">
                              {loc.services.map((s) => (
                                <Badge key={s} variant="secondary" className="text-xs bg-slate-100 text-slate-600">{s}</Badge>
                              ))}
                            </div>
                          )}
                          {(loc.tsp_id || loc.tw_dc || loc.sub_agent_id || loc.corporation_name) && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 ml-12 p-3 bg-slate-50 rounded-lg border border-slate-100 text-xs" data-testid={`location-ids-${loc.id}`}>
                              <div>
                                <p className="text-slate-400">TSP ID</p>
                                <p className="font-medium text-slate-700">{loc.tsp_id || "—"}</p>
                              </div>
                              <div>
                                <p className="text-slate-400">TW DC</p>
                                <p className="font-medium text-slate-700">{loc.tw_dc || "—"}</p>
                              </div>
                              <div>
                                <p className="text-slate-400">Sub-Agent ID</p>
                                <p className="font-medium text-slate-700">{loc.sub_agent_id || "—"}</p>
                              </div>
                              <div>
                                <p className="text-slate-400">Corporation Name</p>
                                <p className="font-medium text-slate-700">{loc.corporation_name || "—"}</p>
                              </div>
                            </div>
                          )}
                          {(loc.vidapay_status || loc.vidapay_tsp_id || loc.vidapay_username || loc.b2bsoft_client_id || loc.b2bsoft_access_code || loc.b2bsoft_username) && (
                            <div className="mt-3 ml-12 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3" data-testid={`external-systems-info-${loc.id}`}>
                              <div className="flex items-center gap-1.5">
                                <Zap className="w-3.5 h-3.5 text-slate-600" />
                                <span className="text-xs font-semibold text-slate-700">External Systems</span>
                              </div>

                              {(loc.vidapay_status || loc.vidapay_tsp_id || loc.vidapay_username) && (
                                <div className="p-2.5 bg-blue-50/60 rounded-md border border-blue-100" data-testid={`vidapay-info-${loc.id}`}>
                                  <p className="text-[11px] font-semibold text-blue-700 mb-1.5">VidaPay</p>
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                    <div>
                                      <p className="text-slate-400">Status</p>
                                      <p className="font-medium text-slate-700">{loc.vidapay_status ? (
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${loc.vidapay_status === "active" ? "bg-emerald-100 text-emerald-700" : loc.vidapay_status === "cash_only" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{loc.vidapay_status === "cash_only" ? "Cash Only" : loc.vidapay_status}</span>
                                      ) : "—"}</p>
                                    </div>
                                    <div>
                                      <p className="text-slate-400">TSP ID</p>
                                      <p className="font-medium text-slate-700">{loc.vidapay_tsp_id || "—"}</p>
                                    </div>
                                    <div>
                                      <p className="text-slate-400">Username</p>
                                      <p className="font-medium text-slate-700">{loc.vidapay_username || "—"}</p>
                                    </div>
                                    <div>
                                      <p className="text-slate-400">Password</p>
                                      <RevealableSecret value={loc.vidapay_password} testid={`vidapay-password-cell-${loc.id}`} />
                                    </div>
                                  </div>
                                </div>
                              )}

                              {(loc.b2bsoft_client_id || loc.b2bsoft_access_code || loc.b2bsoft_username) && (
                                <div className="p-2.5 bg-purple-50/60 rounded-md border border-purple-100" data-testid={`b2bsoft-info-${loc.id}`}>
                                  <p className="text-[11px] font-semibold text-purple-700 mb-1.5">B2BSoft</p>
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                    <div>
                                      <p className="text-slate-400">Client ID</p>
                                      <p className="font-medium text-slate-700 break-all">{loc.b2bsoft_client_id || "—"}</p>
                                    </div>
                                    <div>
                                      <p className="text-slate-400">Access Code</p>
                                      <p className="font-medium text-slate-700 break-all">{loc.b2bsoft_access_code || "—"}</p>
                                    </div>
                                    <div>
                                      <p className="text-slate-400">Username</p>
                                      <p className="font-medium text-slate-700">{loc.b2bsoft_username || "—"}</p>
                                    </div>
                                    <div>
                                      <p className="text-slate-400">Password</p>
                                      <RevealableSecret value={loc.b2bsoft_password} testid={`b2bsoft-password-cell-${loc.id}`} />
                                    </div>
                                  </div>
                                </div>
                              )}

                              {isOwner && <ExternalSystemsEditor location={loc} onSave={fetchAll} />}
                            </div>
                          )}
                          <ContactEditor location={loc} onSave={fetchAll} />
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-800 text-xs flex-shrink-0">Active</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </div>
        )}

        {activeTab === "submitted" && (
          <div className="space-y-3" data-testid="submitted-locations-list">
            {submitted.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 font-body text-lg">No location submissions yet</p>
                  <p className="text-slate-400 font-body text-sm mt-1">Submit a new location request from the "Submit New Location" page</p>
                </CardContent>
              </Card>
            ) : (
              submitted.map((lead, i) => {
                const details = parseNotes(lead.notes);
                return (
                  <motion.div key={lead.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <Card className="border-slate-200" data-testid={`submitted-location-${lead.id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-5 h-5 text-amber-600" />
                              </div>
                              <div>
                                <h3 className="font-heading font-semibold text-navy">{details.store_name || lead.company || "Location Submission"}</h3>
                                <p className="text-sm text-slate-500 font-body">
                                  {[details.address, details.city, details.state, details.zip].filter(Boolean).join(", ") || "Address pending"}
                                </p>
                              </div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-2 mt-3 pl-12">
                              {details.phone && (
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                  <Phone className="w-3.5 h-3.5 text-slate-400" />{details.phone}
                                </div>
                              )}
                              {details.hours && (
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                  <Clock className="w-3.5 h-3.5 text-slate-400" />{details.hours}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-slate-400 mt-2 pl-12">
                              Submitted {new Date(lead.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <StatusBadge status={lead.status} />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })
            )}
          </div>
        )}
      </div>
    </DealerLayout>
  );
}
